import React from 'react';
import { Outlet } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import LibrarianSidebar from './LibrarianSidebar';
import LibrarianTopbar from './LibrarianTopbar';
import { LibrarianTopbarProvider, useLibrarianTopbar } from '@/contexts/LibrarianTopbarContext';
import { StaffAreaProvider } from '@/contexts/StaffAreaContext';
import { LIB_THEME_CSS } from '@/styles/lib-theme';
import CommandPalette, { type PaletteItem } from '@/components/shared/CommandPalette';
import KeyboardHelpOverlay from '@/components/shared/KeyboardHelpOverlay';
import { useKeyboardNav, type GotoShortcut } from '@/hooks/use-keyboard-nav';
import {
  LayoutDashboard, BookOpen, UserSquare2, Building2, Tag, Sparkles,
  MapPin, ArrowLeftRight, Users, Receipt, Mail, ListTodo, BarChart3,
  Bell, Settings,
} from 'lucide-react';

const LIBRARIAN_PALETTE_ITEMS: PaletteItem[] = [
  { label: 'Dashboard',       href: '/librarian',                  icon: LayoutDashboard, shortcut: 'G D', group: 'Overview'    },
  { label: 'Books',           href: '/librarian/books',            icon: BookOpen,        shortcut: 'G B', group: 'Collection'  },
  { label: 'Authors',         href: '/librarian/authors',          icon: UserSquare2,     shortcut: 'G A', group: 'Collection'  },
  { label: 'Publishers',      href: '/librarian/publishers',       icon: Building2,       shortcut: 'G P', group: 'Collection'  },
  { label: 'Genres',          href: '/librarian/genres',           icon: Tag,                              group: 'Collection'  },
  { label: 'Conditions',      href: '/librarian/conditions',       icon: Sparkles,                         group: 'Collection'  },
  { label: 'Locations',       href: '/librarian/locations',        icon: MapPin,                           group: 'Collection'  },
  { label: 'Loans',           href: '/librarian/loans',            icon: ArrowLeftRight,  shortcut: 'G L', group: 'Operations' },
  { label: 'Members',         href: '/librarian/members',          icon: Users,           shortcut: 'G M', group: 'Operations' },
  { label: 'Penalties',       href: '/librarian/penalties',        icon: Receipt,                          group: 'Operations' },
  { label: 'Overdue notices', href: '/librarian/overdue-notices',  icon: Mail,                             group: 'Operations' },
  { label: 'Tasks',           href: '/librarian/tasks',            icon: ListTodo,        shortcut: 'G T', group: 'Operations' },
  { label: 'Analytics',       href: '/librarian/analytics',        icon: BarChart3,                        group: 'System'     },
  { label: 'Notifications',   href: '/librarian/notifications',    icon: Bell,            shortcut: 'G N', group: 'System'     },
  { label: 'Settings',        href: '/librarian/settings',         icon: Settings,        shortcut: 'G S', group: 'System'     },
];

const LIBRARIAN_GOTO: GotoShortcut[] = [
  { key: 'D', label: 'Dashboard',     href: '/librarian'               },
  { key: 'B', label: 'Books',         href: '/librarian/books'         },
  { key: 'A', label: 'Authors',       href: '/librarian/authors'       },
  { key: 'P', label: 'Publishers',    href: '/librarian/publishers'    },
  { key: 'L', label: 'Loans',         href: '/librarian/loans'         },
  { key: 'M', label: 'Members',       href: '/librarian/members'       },
  { key: 'T', label: 'Tasks',         href: '/librarian/tasks'         },
  { key: 'N', label: 'Notifications', href: '/librarian/notifications' },
  { key: 'S', label: 'Settings',      href: '/librarian/settings'      },
];

const LibrarianLayoutInner: React.FC = () => {
  const { helpOpen, closeHelp } = useKeyboardNav({ shortcuts: LIBRARIAN_GOTO });
  const topbar = useLibrarianTopbar();
  return (
    <StaffAreaProvider base="/librarian" topbar={topbar}>
      <CommandPalette items={LIBRARIAN_PALETTE_ITEMS} variant="lib" />
      {helpOpen && (
        <KeyboardHelpOverlay shortcuts={LIBRARIAN_GOTO} onClose={closeHelp} variant="lib" />
      )}
      <div className="librarian-app h-screen grid grid-cols-[224px_1fr] overflow-hidden">
        <LibrarianSidebar />
        <div className="flex flex-col min-w-0 min-h-0">
          <LibrarianTopbar />
          <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            <Outlet />
          </main>
        </div>
        <Toaster />
      </div>
    </StaffAreaProvider>
  );
};

const LibrarianLayout: React.FC = () => (
  <LibrarianTopbarProvider>
    <style>{LIB_THEME_CSS}</style>
    <LibrarianLayoutInner />
  </LibrarianTopbarProvider>
);

export default LibrarianLayout;
